/**
 * API Service Layer for KursAdmin
 * Centralized API calls with error handling and token management
 */

import axios from 'axios';
import { CONTACT_API_URL, COURSE_API_URL, EMAIL_API_URL, environment } from '../lib/api-config';

const API_URLS = {
  contact: CONTACT_API_URL,
  course: COURSE_API_URL,
  email: EMAIL_API_URL
};

// Log environment on load (only in non-production)
if (environment !== 'production') {
  console.log(`[API] Environment: ${environment}`);
  console.log(`[API] URLs:`, API_URLS);
}

// Create axios instance with defaults (httpOnly cookie auth)
const createApiClient = (baseURL) => {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle 401 - redirect to login
      if (error.response?.status === 401) {
        // Rensa eventuell kvarvarande localStorage från före migrering
        localStorage.removeItem('kursadmin_token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// API clients
const contactApi = createApiClient(API_URLS.contact);
const courseApi = createApiClient(API_URLS.course);
// emailApi reserved for future email-api integration
const emailApi = createApiClient(API_URLS.email); // eslint-disable-line no-unused-vars

/**
 * Extract error message from API error
 * @param {Error} error - Axios error
 * @returns {string} Error message
 */
const fieldTranslations = {
  'course_id': 'Kursmall', 'location': 'Plats', 'city': 'Stad',
  'start_date': 'Startdatum', 'end_date': 'Slutdatum', 'name': 'Namn',
  'email': 'E-post', 'price': 'Pris'
};

export const getErrorMessage = (error) => {
  let msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Ett oväntat fel inträffade';
  // Translate Sequelize NotNull violations
  if (msg.includes('notNull Violation') || msg.includes('NotNull Violation')) {
    const fields = [...msg.matchAll(/\.(\w+) cannot be null/g)].map(m => {
      const field = m[1].replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
      return fieldTranslations[field] || m[1];
    });
    return fields.length ? `Obligatoriska fält saknas: ${fields.join(', ')}` : 'Obligatoriska fält saknas';
  }
  return msg;
};

// ========== AUTH API ==========

export const authApi = {
  login: (email, password) =>
    contactApi.post('/auth/login', { email, password }),

  getProfile: () =>
    contactApi.get('/auth/me'),

  changePassword: (currentPassword, newPassword) =>
    contactApi.put('/auth/password', { currentPassword, newPassword }),

  getUsers: () =>
    contactApi.get('/auth/users'),

  createUser: (userData) =>
    contactApi.post('/auth/users', userData),

  updateUser: (id, userData) =>
    contactApi.put(`/auth/users/${id}`, userData),

  deleteUser: (id) =>
    contactApi.delete(`/auth/users/${id}`)
};

// ========== CONTACTS API ==========

export const contactsApi = {
  getAll: () =>
    contactApi.get('/contacts'),

  getById: (id) =>
    contactApi.get(`/contacts/${id}`),

  search: (query) =>
    contactApi.get(`/contacts/search/${encodeURIComponent(query)}`),

  create: (contactData) =>
    contactApi.post('/contacts', contactData),

  update: (id, contactData) =>
    contactApi.put(`/contacts/${id}`, contactData),

  delete: (id) =>
    contactApi.delete(`/contacts/${id}`)
};

// ========== COURSES API ==========

export const coursesApi = {
  getAll: () =>
    courseApi.get('/courses'),

  getById: (id) =>
    courseApi.get(`/courses/${id}`),

  create: (courseData) =>
    courseApi.post('/courses', courseData),

  update: (id, courseData) =>
    courseApi.put(`/courses/${id}`, courseData),

  delete: (id) =>
    courseApi.delete(`/courses/${id}`)
};

// ========== INSTANCES API ==========

export const instancesApi = {
  getAll: () =>
    courseApi.get('/instances'),

  getUpcoming: () =>
    courseApi.get('/instances/upcoming'),

  getById: (id) =>
    courseApi.get(`/instances/${id}`),

  create: (instanceData) =>
    courseApi.post('/instances', instanceData),

  update: (id, instanceData) =>
    courseApi.put(`/instances/${id}`, instanceData),

  delete: (id) =>
    courseApi.delete(`/instances/${id}`),

  getSurveyStats: (id) =>
    courseApi.get(`/instances/${id}/survey-stats`),

  sendAttendanceLink: (id) =>
    courseApi.post(`/instances/${id}/send-attendance-link`),

  lockAttendance: (id) =>
    courseApi.post(`/instances/${id}/lock-attendance`)
};

// ========== BOOKINGS API ==========

export const bookingsApi = {
  getAll: (params = {}) =>
    courseApi.get('/bookings', { params }),

  getById: (id) =>
    courseApi.get(`/bookings/${id}`),

  getByInstance: (instanceId) =>
    courseApi.get(`/bookings/instance/${instanceId}`),

  update: (id, bookingData) =>
    courseApi.put(`/bookings/${id}`, bookingData),

  cancel: (id, reason) =>
    courseApi.put(`/bookings/${id}/cancel`, { cancellationReason: reason }),

  delete: (id) =>
    courseApi.delete(`/bookings/${id}`)
};

// ========== VENUES API ==========

export const venuesApi = {
  getAll: () =>
    courseApi.get('/venues'),

  create: (venueData) =>
    courseApi.post('/venues', venueData),

  update: (id, venueData) =>
    courseApi.put(`/venues/${id}`, venueData),

  delete: (id) =>
    courseApi.delete(`/venues/${id}`)
};

// ========== DISCOUNT CODES API ==========

export const discountCodesApi = {
  getAll: () =>
    courseApi.get('/discount-codes'),

  getById: (id) =>
    courseApi.get(`/discount-codes/${id}`),

  create: (codeData) =>
    courseApi.post('/discount-codes', codeData),

  update: (id, codeData) =>
    courseApi.put(`/discount-codes/${id}`, codeData),

  delete: (id) =>
    courseApi.delete(`/discount-codes/${id}`)
};

// ========== DASHBOARD API ==========

export const dashboardApi = {
  getData: () =>
    courseApi.get('/dashboard')
};

// ========== GDPR API ==========

export const gdprApi = {
  exportData: (email) =>
    courseApi.get(`/gdpr/export/${encodeURIComponent(email)}`),

  eraseData: (email, reason) =>
    courseApi.delete(`/gdpr/erase/${encodeURIComponent(email)}`, { data: { reason } }),

  getRetentionReport: (months = 24) =>
    courseApi.get('/retention/report', { params: { months } }),

  runRetentionCleanup: (months = 24) =>
    courseApi.post('/retention/cleanup', { months })
};

// ========== REPORTS API (course-api) ==========

export const reportsApi = {
  getPreCourse: (instanceId) =>
    courseApi.get(`/reports/pre-course/${instanceId}`)
};

// ========== SMS API ==========

export const smsApi = {
  sendToInstance: (instanceId, message, includeWaitlist = false) =>
    courseApi.post(`/sms/send-to-instance/${instanceId}`, { message, includeWaitlist }),

  sendToBooking: (bookingId, message) =>
    courseApi.post(`/sms/send-to-booking/${bookingId}`, { message }),

  getStatus: () =>
    courseApi.get('/sms/status')
};

// ========== SCHEDULER API ==========

export const schedulerApi = {
  runAll: () =>
    courseApi.post('/scheduler/run'),

  runJob: (jobType) =>
    courseApi.post(`/scheduler/run/${jobType}`)
};

// Export default object with all APIs
const api = {
  auth: authApi,
  contacts: contactsApi,
  courses: coursesApi,
  instances: instancesApi,
  bookings: bookingsApi,
  venues: venuesApi,
  discountCodes: discountCodesApi,
  dashboard: dashboardApi,
  gdpr: gdprApi,
  sms: smsApi,
  scheduler: schedulerApi,
  reports: reportsApi,
  getErrorMessage
};

export default api;
